import { supabase } from './supabase';

const PHOTO_BUCKET = 'penduduk-photos';
const VIDEO_BUCKET = 'cctv-videos';

// All accepted video formats — uploaded directly to Supabase storage.
// Browser playback compatibility is handled per-codec: mp4/webm/m4v play
// natively; mov is fine in Chromium when the codec is H.264; mkv/wmv/etc.
// are stored as-is (operator can convert later if needed).
const VIDEO_EXT_WHITELIST = ['mp4', 'webm', 'm4v', 'mov', 'avi', 'mkv', 'wmv', 'flv', '3gp', 'mpeg', 'mpg'];
const PHOTO_EXT_WHITELIST = ['jpg', 'jpeg', 'png', 'webp'];

function fetchWithTimeout(url, options = {}, ms = 15000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { ...options, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

// ── Face / HF Space ───────────────────────────────────────────────────────────

export async function syncFaceToHF(nik, fotoUrl, slot = 0) {
  // Lewat proxy /api/sync-faces (ber-sesi, bawa HF_TOKEN di server). Space privat: panggilan langsung 404.
  try {
    const res = await fetchWithTimeout('/api/sync-faces', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nik, foto_url: fotoUrl, slot }),
    }, 30000);
    if (!res.ok) {
      console.warn('[syncFaceToHF] HTTP', res.status);
      return { embedded: false, error: `HTTP ${res.status}` };
    }
    const result = await res.json();
    // Bidirectional sync: re-extract per-slot embedding, average all slots,
    // and persist to Supabase penduduk.face_embedding so HF can rehydrate
    // after restart and client-side cosine match works without HF face_db.
    syncSupabaseEmbedding(nik).catch(e => console.warn('[syncSupabaseEmbedding] background fail:', e?.message));
    return result;
  } catch (e) {
    console.warn('[syncFaceToHF] fail:', e?.message || e);
    return { embedded: false, error: e?.message || 'network' };
  }
}

// Walk through every slot photo for a NIK, extract embedding via HF, average them,
// L2-normalize, write to penduduk.face_embedding. Idempotent.
export async function syncSupabaseEmbedding(nik) {
  // NB: jalur ekstraksi pakai proxy /api/extract-embedding (ber-auth, server-side),
  // BUKAN HF_URL langsung — jadi jangan gate dengan HF_URL (env itu cuma utk display).
  if (!nik) return false;
  const { data: files, error: lerr } = await supabase.storage.from(PHOTO_BUCKET).list(nik);
  if (lerr) { console.warn('[syncSupabaseEmbedding] list:', lerr.message); return false; }
  const photos = (files || []).filter(f => /\.(jpg|jpeg|png|webp)$/i.test(f.name));
  if (!photos.length) return false;

  const embs = [];
  for (const f of photos) {
    const url = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(`${nik}/${f.name}`).data.publicUrl;
    try {
      const imgRes = await fetch(url);
      if (!imgRes.ok) continue;
      const blob = await imgRes.blob();
      const fd = new FormData();
      fd.append('file', blob, f.name);
      const er = await fetchWithTimeout('/api/extract-embedding', { method: 'POST', body: fd }, 30000);
      if (!er.ok) continue;
      const j = await er.json();
      if (j.detected && Array.isArray(j.embedding)) embs.push(j.embedding);
    } catch (e) { console.warn('[syncSupabaseEmbedding] slot fail:', e?.message); }
  }
  if (!embs.length) return false;

  // Average + L2 normalize
  const dim = embs[0].length;
  const avg = new Array(dim).fill(0);
  for (const e of embs) for (let i = 0; i < dim; i++) avg[i] += e[i];
  for (let i = 0; i < dim; i++) avg[i] /= embs.length;
  const norm = Math.sqrt(avg.reduce((a, b) => a + b * b, 0)) || 1;
  const normalized = avg.map(x => x / norm);

  // rowcount guard: .select() supaya tahu baris benar ter-update. Kalau 0 baris
  // (NIK salah/stale) JANGAN klaim sukses — cegah false-positive seperti writer lain.
  const { data: upd, error: uerr } = await supabase.from('penduduk')
    .update({ face_embedding: normalized })
    .eq('nik', nik)
    .select('nik');
  if (uerr) { console.warn('[syncSupabaseEmbedding] update:', uerr.message); return false; }
  if (!upd || upd.length === 0) { console.warn('[syncSupabaseEmbedding] 0 rows for', nik); return false; }
  return true;
}

// Bootstrap HF face_db from all Supabase embeddings.
// DISABLED 2026-05-19: HF Space KES_CYBERCITY tidak punya endpoint /import-embeddings
// (404 setiap boot, bikin console noise). Face matching primary path = client-side
// cosine via embeddingsRef yang di-load langsung dari Supabase di aiScanner.js
// (jF/jhPing → embSyncCheckAndFill). HF face_db ephemeral anyway — reset tiap
// container restart. Skip bootstrap entirely sebagai no-op.
export async function bootstrapHFFromSupabase() {
  return { skipped: 'not_needed', reason: 'client_side_match_primary' };
}

export async function deleteFaceFromHF(nik, slot) {
  try {
    const res = await fetchWithTimeout('/api/delete-face', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nik, slot }),
    }, 15000);
    return { ok: res.ok };
  } catch (e) {
    console.warn('[deleteFaceFromHF] fail:', e?.message || e);
    return { ok: false, error: e?.message || 'network' };
  }
}

export { VIDEO_EXT_WHITELIST, PHOTO_EXT_WHITELIST };

// ── Foto Penduduk (multi-photo per NIK) ───────────────────────────────────────

export function getPhotoUrl(path) {
  const { data } = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

// Upload satu foto ke slot tertentu (0–5) → {path, url, slot}
export async function uploadPhotoMulti(file, nik, slot) {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  if (!PHOTO_EXT_WHITELIST.includes(ext)) {
    throw new Error(`Format foto tidak didukung: .${ext}`);
  }
  const path = `${nik}/${slot}.${ext}`;
  const { error } = await supabase.storage
    .from(PHOTO_BUCKET)
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  return { path, url: getPhotoUrl(path), slot };
}

// List semua foto untuk 1 NIK → [{path, url, slot, name}]
export async function listPhotos(nik) {
  const { data } = await supabase.storage.from(PHOTO_BUCKET).list(nik);
  if (!data) return [];
  return data
    .filter(f => /\.(jpg|jpeg|png|webp)$/i.test(f.name))
    .map(f => ({
      path: `${nik}/${f.name}`,
      url:  getPhotoUrl(`${nik}/${f.name}`),
      slot: parseInt(f.name.split('.')[0]) || 0,
      name: f.name,
    }))
    .sort((a, b) => a.slot - b.slot);
}

// Hapus satu foto by storage path
export async function deletePhotoSlot(path) {
  const { error } = await supabase.storage.from(PHOTO_BUCKET).remove([path]);
  if (error) throw error;
}

// Kept for backward compat (single-photo upload, slot 0)
export async function uploadPhoto(file, nik) {
  const { url } = await uploadPhotoMulti(file, nik, 0);
  return url;
}

// Kept for backward compat
export async function deletePhoto(path) {
  return deletePhotoSlot(path);
}

// ── Video CCTV ────────────────────────────────────────────────────────────────

export async function uploadVideo(file, cameraId) {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (!VIDEO_EXT_WHITELIST.includes(ext)) {
    throw new Error(`Format video tidak didukung: .${ext}`);
  }
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${cameraId}/${Date.now()}_${safeName}`;
  const { error } = await supabase.storage
    .from(VIDEO_BUCKET)
    .upload(path, file, { upsert: false, contentType: file.type });
  if (error) throw error;
  return getVideoUrl(path);
}

export function getVideoUrl(path) {
  const { data } = supabase.storage.from(VIDEO_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

export async function listVideos(cameraId) {
  const { data, error } = await supabase.storage
    .from(VIDEO_BUCKET)
    .list('', { search: cameraId });
  if (error) throw error;
  return (data ?? []).map(f => ({
    name: f.name,
    url: getVideoUrl(f.name),
    size: f.metadata?.size ?? 0,
    created_at: f.created_at,
  }));
}

export async function deleteVideo(path) {
  const { error } = await supabase.storage.from(VIDEO_BUCKET).remove([path]);
  if (error) throw error;
}
